import React, { useState, useMemo } from 'react';
import { Card, Table, Pagination, Spinner } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const ITEMS_PER_PAGE = 50;

// W-0743 — Grid de resultado com ordenação + paginação local. Key estável (FE-001).
// Colunas condicionais: imprimir_codigo_cliente (GE/Conta) e listar_m2_endereco (Área + imóvel). Fonte: pas:1023-1027 / DFM:546.
const formatarData = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const GridResultado = ({ itens, listarM2Endereco, imprimirCodigoCliente, loading }) => {
  const [ordenacao, setOrdenacao] = useState({ campo: '', direcao: 'asc' });
  const [pagina, setPagina] = useState(1);

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) =>
      prev.campo === campo
        ? { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' }
    );
  };

  const icone = (campo) => {
    if (ordenacao.campo !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    return ordenacao.direcao === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const ordenados = useMemo(() => {
    const lista = Array.isArray(itens) ? itens : [];
    if (!ordenacao.campo) return lista;
    return [...lista].sort((a, b) => {
      const va = a[ordenacao.campo];
      const vb = b[ordenacao.campo];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return ordenacao.direcao === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (sa > sb) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }, [itens, ordenacao]);

  const total = ordenados.length;
  const totalPaginas = Math.ceil(total / ITEMS_PER_PAGE);
  const paginaAtual = Math.min(pagina, totalPaginas || 1);
  const itensPagina = ordenados.slice(
    (paginaAtual - 1) * ITEMS_PER_PAGE,
    paginaAtual * ITEMS_PER_PAGE
  );

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    items.push(
      <Pagination.First key="first" onClick={() => setPagina(1)} disabled={paginaAtual === 1} />,
      <Pagination.Prev key="prev" onClick={() => setPagina(paginaAtual - 1)} disabled={paginaAtual === 1} />
    );
    const ini = Math.max(1, paginaAtual - 2);
    const fim = Math.min(totalPaginas, paginaAtual + 2);
    if (ini > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let i = ini; i <= fim; i += 1) {
      items.push(
        <Pagination.Item key={i} active={i === paginaAtual} onClick={() => setPagina(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" onClick={() => setPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} />,
      <Pagination.Last key="last" onClick={() => setPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} />
    );
    return <Pagination size="sm" className="mb-0 flex-wrap">{items}</Pagination>;
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0">Carregando...</p>
        </Card.Body>
      </Card>
    );
  }

  if (total === 0) return null;

  return (
    <Card className="shadow-sm atu-cad-grid-card">
      <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff', fontWeight: 'bold' }}>
        Resultado | {total} registro(s)
      </Card.Header>
      <Card.Body className="p-0">
        <div className="atu-cad-desktop">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th style={thStyle} onClick={() => handleOrdenacao('data_pre_venda')}>Dt. Cont. {icone('data_pre_venda')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('nome_da_familia')}>Família {icone('nome_da_familia')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('nome_ativo')}>Imóvel {icone('nome_ativo')}</th>
                {imprimirCodigoCliente && (
                  <th style={thStyle} onClick={() => handleOrdenacao('codigo_grupo_empresa')}>GE {icone('codigo_grupo_empresa')}</th>
                )}
                {imprimirCodigoCliente && (
                  <th style={thStyle} onClick={() => handleOrdenacao('codigo_conta')}>Conta {icone('codigo_conta')}</th>
                )}
                <th style={thStyle} onClick={() => handleOrdenacao('apelido_conta')}>Cliente {icone('apelido_conta')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('mapa1')}>QD {icone('mapa1')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('mapa2')}>LT {icone('mapa2')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('cad_prefeitura')}>Cad. Pref. {icone('cad_prefeitura')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('cad_agua')}>Cad. Água {icone('cad_agua')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('movimento')}>Movimento {icone('movimento')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('cpf')}>CPF {icone('cpf')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('endereco')}>Endereço {icone('endereco')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('bairro')}>Bairro {icone('bairro')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('cep')}>CEP {icone('cep')}</th>
                <th style={thStyle} onClick={() => handleOrdenacao('descricao_cidade')}>Cidade {icone('descricao_cidade')}</th>
                {listarM2Endereco && (
                  <th style={thStyle} onClick={() => handleOrdenacao('area_total')}>Área M² {icone('area_total')}</th>
                )}
                {listarM2Endereco && <th>End. Imóvel</th>}
                {listarM2Endereco && <th>Bairro Imóvel</th>}
                {listarM2Endereco && <th>Cidade Imóvel</th>}
                {listarM2Endereco && <th>UF Imóvel</th>}
                {listarM2Endereco && <th>CEP Imóvel</th>}
              </tr>
            </thead>
            <tbody>
              {itensPagina.map((item) => (
                <tr key={`${item.codigo_boleto}_${item.codigo_ativo}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatarData(item.data_pre_venda)}</td>
                  <td>{item.nome_da_familia}</td>
                  <td>{item.nome_ativo}</td>
                  {imprimirCodigoCliente && <td>{item.codigo_grupo_empresa}</td>}
                  {imprimirCodigoCliente && <td>{item.codigo_conta}</td>}
                  <td>{item.apelido_conta}</td>
                  <td>{item.mapa1}</td>
                  <td>{item.mapa2}</td>
                  <td>{item.cad_prefeitura}</td>
                  <td>{item.cad_agua}</td>
                  <td>{item.movimento}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{item.cpf}</td>
                  <td>{item.endereco}</td>
                  <td>{item.bairro}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{item.cep}</td>
                  <td>{item.descricao_cidade}{item.estado ? `/${item.estado}` : ''}</td>
                  {listarM2Endereco && <td style={{ textAlign: 'right' }}>{item.area_total}</td>}
                  {listarM2Endereco && <td>{item.imovel_endereco}</td>}
                  {listarM2Endereco && <td>{item.imovel_bairro}</td>}
                  {listarM2Endereco && <td>{item.imovel_cidade}</td>}
                  {listarM2Endereco && <td>{item.imovel_estado}</td>}
                  {listarM2Endereco && <td style={{ whiteSpace: 'nowrap' }}>{item.imovel_cep}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {totalPaginas > 1 && (
          <div className="d-flex justify-content-between align-items-center px-3 py-2">
            <small className="text-muted">
              Mostrando {((paginaAtual - 1) * ITEMS_PER_PAGE) + 1} até{' '}
              {Math.min(paginaAtual * ITEMS_PER_PAGE, total)} de {total} registros
            </small>
            {renderPaginacao()}
          </div>
        )}
      </Card.Body>
      <style>{`
        .atu-cad-desktop { display: block; overflow-x: auto; }
        .atu-cad-grid-card { max-width: calc(100vw - 50px); }
      `}</style>
    </Card>
  );
};

export default GridResultado;
